<template>
    <AdminLayout :crumbs="[{ label: 'Ajustes generales' }]">
        <div class="a-page-head">
            <div>
                <h1 class="a-page-title">Ajustes generales</h1>
                <p class="a-page-subtitle">Lo que ven los jugadores en todos los {{ season_label_plural.toLowerCase() }}: logos, colores e imágenes de fondo.</p>
            </div>
        </div>

        <div class="settings-grid">
            <nav class="settings-nav">
                <a v-for="s in sections" :key="s.id" :href="'#' + s.id" @click.prevent="go(s.id)">{{ s.label }}</a>
            </nav>

            <div>
                <!-- Logos -->
                <section class="a-card mb-4" id="sec-logos">
                    <div class="a-card-header">
                        <div>
                            <h2 class="a-section-title">Logos de patrocinadores</h2>
                            <div class="a-subtle">Se van alternando en el pie de las pantallas de juego.</div>
                        </div>
                    </div>
                    <ul class="a-list" v-if="logos.length">
                        <li class="a-row" v-for="(logo, i) in logos" :key="logo + i">
                            <img :src="logo" class="logo-thumb" alt="">
                            <div class="a-row-main"><div class="a-row-title a-muted fw-normal">{{ logo }}</div></div>
                            <button type="button" class="btn btn-ghost btn-icon" title="Quitar" @click="remove_logo(i)"><i class="bi bi-x-lg"></i></button>
                        </li>
                    </ul>
                    <div class="a-card-body">
                        <div class="input-group">
                            <input type="url" class="form-control" v-model="logo_url" placeholder="https://… URL de la imagen del logo" @keyup.enter="add_logo">
                            <button type="button" class="btn btn-secondary" @click="add_logo"><i class="bi bi-plus-lg"></i>Añadir</button>
                        </div>
                    </div>
                </section>

                <!-- Colours -->
                <section class="a-card mb-4" id="sec-colores">
                    <div class="a-card-header">
                        <div>
                            <h2 class="a-section-title">Colores</h2>
                            <div class="a-subtle">Textos y barras de las pantallas de juego.</div>
                        </div>
                        <button type="button" class="btn btn-primary btn-sm" :disabled="!styles_dirty" @click="save_styles">Guardar colores</button>
                    </div>
                    <div class="a-card-body" v-if="styles">
                        <div v-for="group in style_groups" :key="group.title" class="mb-3">
                            <div class="a-eyebrow mb-2">{{ group.title }}</div>
                            <div class="color-grid">
                                <label v-for="f in group.fields" :key="f.key + f.prop" class="color-field">
                                    <input type="color" v-model="styles[f.key][f.prop]">
                                    <span>{{ f.label }}</span>
                                </label>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- Background images -->
                <section class="a-card mb-4" id="sec-imagenes">
                    <div class="a-card-header">
                        <div>
                            <h2 class="a-section-title">Imágenes de fondo</h2>
                            <div class="a-subtle">JPG, PNG o GIF. Se suben al pulsar Guardar.</div>
                        </div>
                        <button type="button" class="btn btn-primary btn-sm" :disabled="!images_dirty" @click="save_images">Guardar imágenes</button>
                    </div>
                    <div class="a-card-body img-grid">
                        <div v-for="img in image_fields" :key="img.key" class="img-slot">
                            <div class="img-preview">
                                <img v-if="images[img.key]" :src="images[img.key]" alt="">
                                <i v-else class="bi bi-image"></i>
                            </div>
                            <div class="img-label">{{ img.label }}</div>
                            <div class="d-flex gap-2">
                                <label class="btn btn-secondary btn-sm mb-0">
                                    {{ images[img.key] ? 'Cambiar' : 'Subir' }}
                                    <input type="file" class="d-none" accept="image/jpeg,image/jpg,image/png,image/gif" @change="pick_image($event, img.key)">
                                </label>
                                <button v-if="images[img.key]" type="button" class="btn btn-ghost btn-sm" @click="remove_image(img.key)">Quitar</button>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- General join code -->
                <section class="a-card" id="sec-acceso">
                    <div class="a-card-header">
                        <div>
                            <h2 class="a-section-title">Código de acceso general</h2>
                            <div class="a-subtle">Para encontrar la app más rápido. Cada {{ season_label.toLowerCase() }} tiene además su propio código (en su pestaña).</div>
                        </div>
                        <button type="button" class="btn btn-ghost btn-sm" @click="send('REGENERATE_JOIN_CODE', null)"><i class="bi bi-arrow-repeat"></i>Generar nuevo</button>
                    </div>
                    <div class="a-card-body text-center">
                        <JoinCodeQR :size="180" />
                    </div>
                </section>
            </div>
        </div>
    </AdminLayout>
</template>

<script>
import utils from '@/../../shared/utils'
import AdminLayout from '@/components/admin/AdminLayout.vue'
import JoinCodeQR from '@/components/JoinCodeQR.vue'

const IMAGE_FIELDS = [
    { key: 'main_image', label: 'Logo principal' },
    { key: 'lobby_image', label: 'Sala de espera' },
    { key: 'auth_image', label: 'Elección de equipo' },
    { key: 'activity_image', label: 'Actividades' },
    { key: 'ranking_podium_image', label: 'Ranking y podio' },
];

const STYLE_GROUPS = [
    { title: 'Preguntas', fields: [
        { key: 'title', prop: 'color', label: 'Título grande' },
        { key: 'small_title', prop: 'color', label: 'Título pequeño' },
        { key: 'question', prop: 'backgroundColor', label: 'Fondo de la pregunta' },
    ] },
    { title: 'Sala de espera', fields: [
        { key: 'lobby_title', prop: 'color', label: 'Nombre del equipo' },
        { key: 'lobby_wait', prop: 'color', label: 'Texto "espera"' },
    ] },
    { title: 'Ranking y podio', fields: [
        { key: 'ranking_end', prop: 'color', label: 'Títulos' },
        { key: 'ranking_end_teams', prop: 'color', label: 'Equipos' },
        { key: 'ranking_lines', prop: 'backgroundColor', label: 'Barras del ranking' },
        { key: 'end_lines', prop: 'backgroundColor', label: 'Barras del podio' },
    ] },
    { title: 'Carga', fields: [
        { key: 'loading', prop: 'color', label: 'Texto de carga' },
        { key: 'loader', prop: 'borderColor', label: 'Animación de carga' },
    ] },
];

// Game-wide look (not per event): sponsor logos, colours, background images,
// and the general join code. Used to be four modals in a footer menu.
export default {
    name: 'Settings',
    components: { AdminLayout, JoinCodeQR },
    data(){
        return {
            image_fields: IMAGE_FIELDS,
            style_groups: STYLE_GROUPS,
            // Ids prefixed: App.vue styles a global #logos (the game's footer).
            sections: [
                { id: 'sec-logos', label: 'Logos' },
                { id: 'sec-colores', label: 'Colores' },
                { id: 'sec-imagenes', label: 'Imágenes de fondo' },
                { id: 'sec-acceso', label: 'Código de acceso' },
            ],
            logo_url: '',
            styles: null,
            images: {},
        };
    },
    computed: {
        season_label(){ return utils.get_season_label(window.MODE); },
        season_label_plural(){ return utils.get_season_label(window.MODE, true); },
        logos(){ return this.$store.state.game.logos || []; },
        stored_styles(){ return this.merge_styles(this.$store.state.game.styles); },
        styles_dirty(){ return !!this.styles && JSON.stringify(this.styles) != JSON.stringify(this.stored_styles); },
        images_dirty(){
            const stored = this.$store.state.game.images || {};
            return IMAGE_FIELDS.some((f) => (this.images[f.key] || null) != (stored[f.key] || null));
        }
    },
    watch: {
        '$store.state.game.styles': { immediate: true, handler(){ if(!this.styles_dirty) this.styles = JSON.parse(JSON.stringify(this.stored_styles)); } },
        '$store.state.game.images': { immediate: true, handler(stored){ this.images = Object.assign({}, stored || {}); } },
    },
    methods: {
        // Fill any style the stored config lacks from the defaults, so every
        // colour input always has something to bind to.
        merge_styles(stored){
            const out = JSON.parse(JSON.stringify(utils.GAME_STYLES));
            Object.keys(stored || {}).forEach((k) => { out[k] = Object.assign({}, out[k], stored[k]); });
            return out;
        },
        go(id){ document.getElementById(id).scrollIntoView({ behavior: 'smooth', block: 'start' }); },
        send(rpc, params){ window.network.wso.send(JSON.stringify({ action: window.RPC[rpc], params })); },
        add_logo(){
            const url = this.logo_url.trim();
            if(!url) return;
            this.send('UPDATE_LOGOS', this.logos.concat([url]));
            this.logo_url = '';
        },
        remove_logo(i){
            this.send('UPDATE_LOGOS', this.logos.filter((_, idx) => idx != i));
        },
        save_styles(){ this.send('UPDATE_STYLES', this.styles); },
        pick_image(e, key){
            const file = e.target.files[0];
            e.target.value = '';
            if(!file) return;
            const reader = new FileReader();
            reader.onload = (evt) => { this.images = Object.assign({}, this.images, { [key]: evt.target.result }); };
            reader.readAsDataURL(file);
        },
        remove_image(key){
            this.images = Object.assign({}, this.images, { [key]: null });
            this.send('REMOVE_IMAGE', key);
        },
        save_images(){ this.send('UPLOAD_IMAGES', this.images); }
    }
}
</script>

<style lang="scss" scoped>
.settings-grid{
    display: grid;
    grid-template-columns: 180px 1fr;
    gap: 24px;
    align-items: start;
}
.settings-nav{
    position: sticky;
    top: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
    a{
        padding: 6px 10px;
        border-radius: 6px;
        color: var(--a-text-2);
        text-decoration: none;
        font-weight: 600;
        &:hover{ background: rgba(0, 0, 0, 0.05); color: var(--a-text); }
    }
}
.logo-thumb{
    width: 64px;
    height: 36px;
    object-fit: contain;
    background: var(--a-surface-2);
    border-radius: 4px;
    flex-shrink: 0;
}
.color-grid{
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 8px;
}
.color-field{
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 10px 6px 6px;
    border: 1px solid var(--a-line);
    border-radius: 8px;
    cursor: pointer;
    font-size: 0.85rem;
    input{
        width: 32px;
        height: 32px;
        border: 0;
        padding: 0;
        background: none;
        cursor: pointer;
    }
}
.img-grid{
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
    gap: 16px;
}
.img-slot{ display: flex; flex-direction: column; gap: 6px; }
.img-preview{
    aspect-ratio: 16 / 10;
    border-radius: 8px;
    border: 1px solid var(--a-line);
    background: var(--a-surface-2);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    img{ width: 100%; height: 100%; object-fit: cover; }
    .bi{ font-size: 1.6rem; color: var(--a-text-3); }
}
.img-label{ font-weight: 600; font-size: 0.85rem; }
@media (max-width: 991.98px){
    .settings-grid{ grid-template-columns: 1fr; }
    .settings-nav{ display: none; }
}
</style>
